(function() {
    'use strict';
    /*
     * Copyright (c) 2015 In Hand Health.
     * All rights reserved.
     */

    angular
        .module('ihh-common-web')
        .service('IhhMedia', IhhMedia);

    IhhMedia.$inject = ['Utilities', 'IhhFileChooser', 'FileUpload'];

    /**
     * This service contains various methods for handling media operations: uploads, deletes, etc.
     * @ngdoc service
     * @memberof ihh-common-web
     * @name IhhMedia
     */
    function IhhMedia(Utilities, IhhFileChooser, FileUpload) {
        /**
         * Called by the upload methods of this service on a successful upload.
         * @callback ihh-common-web.IhhMedia.SuccessCallback
         * @memberOf ihh-common-web.IhhMedia
         * @name SuccessCallback
         * @param {string} name the name of the uploaded media
         */

        /**
         * Called by the upload methods of this service on a failure to upload.
         * @callback ihh-common-web.IhhMedia.ErrorCallback
         * @memberOf ihh-common-web.IhhMedia
         * @name ErrorCallback
         * @param {Object} err the error object returned from the attempted POST
         */

        /**
         * Called by the upload methods of this service when the upload begins.
         * @callback ihh-common-web.IhhMedia.UploadCallback
         * @memberOf ihh-common-web.IhhMedia
         * @name UploadCallback
         */

        /**
         * Opens a file upload dialog, and if the user chooses a file, attempts to upload that file as image media. If the user cancels
         * the dialog, no action is taken and not callback is fired. Specify a pre-existing image name to replace it.
         * @ngdoc method
         * @name chooseImage
         * @memberOf ihh-common-web.IhhMedia
         * @param {Object} resource a REST resource object for the entity to get the image upload URL
         * @param {string} title a title for the dialog
         * @param {string} message a message shown in the dialog
         * @param {ihh-common-web.IhhMedia.SuccessCallback} successFn called if the image is successfully uploaded
         * @param {ihh-common-web.IhhMedia.ErrorCallback} errorFn called if there is an error uploading the image
         * @param {ihh-common-web.IhhMedia.UploadCallback} uploadFn called when the upload begins
         * @param {Number} if provided, enforces an aspect ratio onto the image upload
         */
        this.chooseImage = function(resource, title, message, successFn, errorFn, uploadFn, aspectRatio) {
            var config = {
                title: title,
                message: message,
                mediaType: 'image',
                aspectRatio: aspectRatio
            };

            this.chooseMedia(resource, config, successFn, errorFn, uploadFn, false);
        };

        /**
         * Opens a file upload dialog, and if the user chooses a file, attempts to upload that file as image media. If the user cancels
         * the dialog, no action is taken and not callback is fired. Specify a pre-existing image name to replace it.
         * @ngdoc method
         * @name chooseImage
         * @memberOf ihh-common-web.IhhMedia
         * @param {Object} resource a REST resource object for the entity to get the image upload URL
         * @param {string} title a title for the dialog
         * @param {string} message a message shown in the dialog
         * @param {ihh-common-web.IhhMedia.SuccessCallback} successFn called if the image is successfully uploaded
         * @param {ihh-common-web.IhhMedia.ErrorCallback} errorFn called if there is an error uploading the image
         * @param {ihh-common-web.IhhMedia.UploadCallback} uploadFn called when the upload begins
         */
        this.chooseThumbnail = function(resource, title, message, successFn, errorFn, uploadFn) {
            var config = {
                title: title,
                message: message,
                mediaType: 'thumbnail',
                aspectRatio: 1 / 1
            };

            this.chooseMedia(resource, config, successFn, errorFn, uploadFn, false);
        };

        /**
         * Opens a file upload dialog, and if the user chooses a file, attempts to upload that file as audio media. If the user cancels
         * the dialog, no action is taken and not callback is fired.
         * @ngdoc method
         * @name chooseAudio
         * @memberOf ihh-common-web.IhhMedia
         * @param {Object} resource a REST resource object for the entity to get the audio upload URL
         * @param {type} title a title for the dialog
         * @param {type} message a message shown in the dialog
         * @param {ihh-common-web.IhhMedia.SuccessCallback} successFn called if the audio is successfully uploaded
         * @param {ihh-common-web.IhhMedia.ErrorCallback} errorFn called if there is an error uploading the audio
         * @param {ihh-common-web.IhhMedia.UploadCallback} uploadFn called when the upload begins
         */
        this.chooseAudio = function(resource, title, message, successFn, errorFn, uploadFn) {
            var config = {
                title: title,
                message: message,
                mediaType: 'audio'
            };

            this.chooseMedia(resource, config, successFn, errorFn, uploadFn, false);
        };

        /**
         * Opens a file upload dialog, and if the user chooses a file, attempts to upload that file as video media. If the user cancels
         * the dialog, no action is taken and not callback is fired.
         * @ngdoc method
         * @name chooseVideo
         * @memberOf ihh-common-web.IhhMedia
         * @param {Object} resource a REST resource object for the entity to get the video upload URL
         * @param {type} title a title for the dialog
         * @param {type} message a message shown in the dialog
         * @param {ihh-common-web.IhhMedia.SuccessCallback} successFn called if the video is successfully uploaded
         * @param {ihh-common-web.IhhMedia.ErrorCallback} errorFn called if there is an error uploading the video
         * @param {ihh-common-web.IhhMedia.UploadCallback} uploadFn called when the upload begins
         */
        this.chooseVideo = function(resource, title, message, successFn, errorFn, uploadFn) {
            var config = {
                title: title,
                message: message,
                mediaType: 'video'
            };

            this.chooseMedia(resource, config, successFn, errorFn, uploadFn, false);
        };

        /**
         * Opens a file upload dialog, and if the user chooses a file, attempts to upload that file as pdf. If the user cancels
         * the dialog, no action is taken and not callback is fired.
         * @ngdoc method
         * @name choosePdf
         * @memberOf ihh-common-web.IhhMedia
         * @param {Object} resource a REST resource object for the entity to get the pdf upload URL
         * @param {type} title a title for the dialog
         * @param {type} message a message shown in the dialog
         * @param {ihh-common-web.IhhMedia.SuccessCallback} successFn called if the video is successfully uploaded
         * @param {ihh-common-web.IhhMedia.ErrorCallback} errorFn called if there is an error uploading the video
         * @param {ihh-common-web.IhhMedia.UploadCallback} uploadFn called when the upload begins
         */
        this.choosePdf = function(resource, title, message, successFn, errorFn, uploadFn, useFilename) {
            var config = {
                title: title,
                message: message,
                mediaType: 'pdf'
            };

            this.chooseMedia(resource, config, successFn, errorFn, uploadFn, !!useFilename);
        };

        /**
         * Opens a file upload dialog, and if the user chooses a file, attempts to upload that file as STL. If the user cancels
         * the dialog, no action is taken and not callback is fired.
         * @ngdoc method
         * @name chooseStl
         * @memberOf ihh-common-web.IhhMedia
         * @param {Object} resource a REST resource object for the entity to get the STL upload URL
         * @param {type} title a title for the dialog
         * @param {type} message a message shown in the dialog
         * @param {ihh-common-web.IhhMedia.SuccessCallback} successFn called if the video is successfully uploaded
         * @param {ihh-common-web.IhhMedia.ErrorCallback} errorFn called if there is an error uploading the video
         * @param {ihh-common-web.IhhMedia.UploadCallback} uploadFn called when the upload begins
         */
        this.chooseStl = function(resource, title, message, successFn, errorFn, uploadFn) {
            var config = {
                title: title,
                message: message,
                mediaType: 'stl',
                contentType: 'model/stl'
            };

            this.chooseMedia(resource, config, successFn, errorFn, uploadFn, true);
        };

        this.chooseMedia = function(resource, config, successFn, errorFn, uploadFn, useFilename) {
            // If the <input type="file"> element doesn't return a file type, this allows the caller to specify a fallback.
            var contentTypeFallback = config.contentType || '';

            IhhFileChooser.open(config, function(file) {
                // Caller can specify to use the filename as the storage name...but this can have unexpected
                // results if the user uploads the same file twice in the same context.
                var name = useFilename && file.name ? file.name : Utilities.randomString(20);
                var contentType = file.type || contentTypeFallback;
                var fileName = file.name;

                resource.save({
                    name: name,
                    contentType: contentType
                }, {}, function(result) {
                    if (result && result.uploadUrl) {
                        if (uploadFn) {
                            uploadFn();
                        }
                        FileUpload.putFileToUrl(file, result.uploadUrl, function( /* result */ ) {
                            successFn(name, contentType, fileName);
                        }, function(err) {
                            errorFn(err);
                        }, contentType);
                    }
                    else {
                        errorFn('No upload URL');
                    }

                }, function(err) {
                    errorFn(err);
                });
            });
        };
    }
})();
